// hooks/usePersistedPreference.js
// ─────────────────────────────────────────────────────────────────────────────
// A view preference of ANY JSON shape that survives a refresh, a navigation
// away and back, and a hot reload. The general form of usePersistedToggle,
// which is now a thin wrapper over this.
//
// REPORTED 2 Oct 2026: the Active / Deactivated / Needs photo / Has photo
// chips on Brand Product Management did not persist. They were `useState(null)`,
// and the one thing an operator does from that page, opening a product to
// edit it, unmounts the page; coming back put every chip off again. "Has photo"
// was the one noticed, but all four behaved the same.
//
// Two things this hook settles that the toggle did not have to:
//
//   perUser   Filters are going to carry ROLE defaults (a supervisor opening
//             the page should land on the view their job needs), and a role
//             default only means something if what one person chose is not
//             what the next person who signs in on the same browser gets. So
//             a per-user preference is keyed by the signed-in user's id; the
//             shared machines in the call centre are exactly where this
//             matters.
//
//   JSON      A chip holds a string or null, a sort holds an object, so the
//             value round-trips through JSON. The toggle's "true"/"false"
//             strings parse as booleans under JSON.parse, so nothing a
//             browser already stored is lost by the wrapper.
//
// Every storage access can throw (private windows, blocked site data, some
// enterprise policies), so every access is wrapped and the fallback is the
// default the caller asked for. A preference that cannot be saved is not
// worth an error.
//
// Use it for the same class of thing as the toggle: a remembered filter, a
// chosen tab, a collapsed section. Not for anything another person or another
// device must see; that belongs on the server.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from "react";
import { signedInIdentity } from "../utilities/identityMemory";

const PREFIX = "lusuku.pref.";

/** The signed-in user's id, or "anon" when nobody is (a key still has to exist).
    One definition of "who is signed in" for every per-person memory. */
export const preferenceOwner = signedInIdentity;

export const preferenceKey = (key, perUser) =>
  perUser ? `${PREFIX}${preferenceOwner()}.${key}` : `${PREFIX}${key}`;

const read = (fullKey, fallback) => {
  try {
    const raw = window.localStorage.getItem(fullKey);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (err) {
    return fallback;
  }
};

const write = (fullKey, value) => {
  try {
    if (value === undefined) window.localStorage.removeItem(fullKey);
    else window.localStorage.setItem(fullKey, JSON.stringify(value));
  } catch (err) {
    /* A preference that cannot be saved is not worth an error. */
  }
};

/**
 * @param {string}  key              stable name, namespaced under lusuku.pref.
 * @param {*}       [defaultValue]   what to use when nothing is stored (any JSON)
 * @param {object}  [options]
 * @param {boolean} [options.perUser] key the value by the signed-in user's id
 * @returns {[*, function]}          same shape as useState
 */
const usePersistedPreference = (key, defaultValue, { perUser = false } = {}) => {
  const fullKey = useMemo(() => preferenceKey(key, perUser), [key, perUser]);
  // Read during the initialiser, not in an effect: an effect would render the
  // default first and then correct it, which is a visible flash of the wrong
  // list on every load.
  const [value, setValue] = useState(() => read(fullKey, defaultValue));

  useEffect(() => {
    write(fullKey, value);
  }, [fullKey, value]);

  const set = useCallback((next) => {
    setValue((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  return [value, set];
};

export default usePersistedPreference;
